import moment from 'moment';

import { moduleToolsAndLanguagesData } from 'bundles/enterprise-learning-path-description-page/data/moduleToolsAndLanguagesData';
import type { VspPageV2Data } from 'bundles/enterprise-learning-path-description-page/module-renderer/EnterpriseSkillPathwayProductOverviewModuleRenderer';
import type { QueryVerifiedSkillPathsPageQuery } from 'bundles/enterprise-learning-path-description-page/queries/__generated__/GetSkillPathwayQueryV2';
import EnterpriseExperiments from 'bundles/epic/clients/Enterprise';

export type VspCourseData = NonNullable<
  NonNullable<QueryVerifiedSkillPathsPageQuery['DescriptionPage']>['queryVerifiedSkillPathsPage'][number]
>;

type VspWeeklyMaterial = NonNullable<NonNullable<VspCourseData['material']>['weeks'][number]>;
type VspLecture = NonNullable<NonNullable<VspWeeklyMaterial['lectures']>[number]>;
type VspPartner = NonNullable<NonNullable<VspLecture['partners']>[number]>;
type VspAssessment = NonNullable<NonNullable<VspWeeklyMaterial['assessments']>[number]>;

const TOOLS_AND_LANGUAGES_MAX_COUNT = 2;
const TOOLS_AND_LANGUAGES_CHARACTER_LIMIT = 20;

export const getInstructorsFromVspPageData = (vspPageData: VspPageV2Data | undefined) => {
  if (!vspPageData) {
    return [];
  }
  const allInstructors = vspPageData.map((course) => course.instructors).flat();
  const seenIds = new Set<string>();

  return allInstructors.filter((instructor) => {
    if (!instructor) {
      return false;
    }
    if (seenIds.has(instructor.id)) {
      return false;
    }
    seenIds.add(instructor.id);
    return true;
  });
};

export const getTimeDurationForVspCourse = (courseData: VspCourseData | undefined): number | null => {
  if (!courseData?.material?.totalDuration) {
    return null;
  }
  const totalDuration = moment.duration(courseData.material.totalDuration);
  return Math.round(totalDuration.asHours());
};

const getPartnersFromVspModuleLecturesData = (vspModuleLecturesData: VspWeeklyMaterial['lectures'] | undefined) => {
  const partnerMap = new Map<string, { name: string; logoUrl: string }>();
  vspModuleLecturesData?.forEach((lecture: VspLecture) => {
    lecture?.partners?.forEach((partner: VspPartner) => {
      if (partner.id && !partnerMap.has(partner.id)) {
        partnerMap.set(partner.id, {
          name: partner.name ?? '',
          logoUrl: partner.squareLogo ?? '',
        });
      }
    });
  });
  return Array.from(partnerMap.values());
};

export const getModulesDataFromVspCourseData = (vspCourseData: VspCourseData | undefined) => {
  if (!vspCourseData?.material?.weeks) {
    return [];
  }
  const modules = vspCourseData.material.weeks;

  const modulesData = modules
    .map((module: VspWeeklyMaterial) => {
      if (!module.lectures) {
        return null;
      }
      return {
        name: module.name,
        duration: Math.round(moment.duration(module.totalDuration).asHours()),
        partners: getPartnersFromVspModuleLecturesData(module.lectures),
        id: module.id,
      };
    })
    .filter(Boolean);
  return modulesData;
};

export const getSkillGoalsFromVspPageData = (vspPageData: VspPageV2Data | undefined) => {
  if (!vspPageData || !Array.isArray(vspPageData)) {
    return {
      totalHours: 0,
      modulesCount: 0,
    };
  }

  const totalHours = vspPageData.reduce((total: moment.Duration, course: VspCourseData) => {
    const courseDuration = course.material?.totalDuration;
    if (!courseDuration) {
      return total;
    }
    return total.add(moment.duration(courseDuration));
  }, moment.duration(0));
  const roundedTotalHours = Math.round(totalHours.asHours());

  return {
    totalHours: roundedTotalHours,
    modulesCount: vspPageData.length,
  };
};

export const getAssessmentsDataFromVspPageData = (vspPageData: VspPageV2Data | undefined) => {
  if (!vspPageData || !Array.isArray(vspPageData)) {
    return {
      totalAssignments: 0,
      totalQuizzes: 0,
    };
  }

  const assessmentsData = {
    totalAssignments: 0,
    totalQuizzes: 0,
  };

  vspPageData.forEach((course: VspCourseData) => {
    course.material?.weeks?.forEach((week: VspWeeklyMaterial) => {
      week.assessments?.forEach((assessment: VspAssessment) => {
        if (assessment.typeName === 'ASSIGNMENT') {
          assessmentsData.totalAssignments += 1;
        }
        if (assessment.typeName === 'QUIZ') {
          assessmentsData.totalQuizzes += 1;
        }
      });
    });
  });

  return assessmentsData;
};

export const getOrderedPartnersFromVspPageData = (vspPageData: VspPageV2Data | undefined) => {
  if (!vspPageData || !Array.isArray(vspPageData)) {
    return [];
  }

  const partnerCountMap = new Map<string, { count: number; partner: VspPartner }>();

  vspPageData.forEach((course: VspCourseData) => {
    course.material?.weeks?.forEach((week: VspWeeklyMaterial) => {
      week.lectures?.forEach((lecture: VspLecture) => {
        lecture.partners?.forEach((partner: VspPartner) => {
          if (partner.id) {
            const existing = partnerCountMap.get(partner.id);
            if (existing) {
              existing.count += 1;
            } else {
              partnerCountMap.set(partner.id, {
                count: 1,
                partner,
              });
            }
          }
        });
      });
    });
  });

  return Array.from(partnerCountMap.values())
    .sort((a, b) => b.count - a.count)
    .map((item) => item.partner);
};

export const getLanguageInfoFromVspPageData = (vspPageData: VspPageV2Data | undefined) => {
  if (!vspPageData || !Array.isArray(vspPageData) || vspPageData.length === 0) {
    return {
      dubbedLanguages: [],
      subtitleLanguages: [],
      primaryLanguages: [],
      translatedLanguages: [],
    };
  }

  // Helper function to find intersection of string arrays
  const getStringArrayIntersection = (arrays: string[][]): string[] => {
    if (arrays.length === 0) {
      return [];
    }
    if (arrays.length === 1) {
      return arrays[0] || [];
    }
    const firstSet = new Set(arrays[0] || []);
    const intersection = arrays.slice(1).reduce((acc, arr) => {
      const currentSet = new Set(arr || []);
      return new Set(Array.from(acc).filter((item) => currentSet.has(item)));
    }, firstSet);
    return Array.from(intersection);
  };

  const dubbedLanguageCodes: string[][] = [];
  const subtitleLanguageCodes: string[][] = [];
  const primaryLanguageCodes: string[][] = [];
  const translatedLanguageCodes: string[][] = [];

  vspPageData.forEach((course: VspCourseData) => {
    const dubbedCodes = course.dubbedLanguages?.map((lang) => lang.languageCode).filter(Boolean) || [];
    dubbedLanguageCodes.push(dubbedCodes);

    subtitleLanguageCodes.push(course.subtitleLanguages || []);

    primaryLanguageCodes.push(course.primaryLanguages || []);

    const translatedCodes = course.translatedLanguages?.map((lang) => lang.code).filter(Boolean) || [];
    translatedLanguageCodes.push(translatedCodes);
  });

  const intersectionDubbedCodes = getStringArrayIntersection(dubbedLanguageCodes);
  const intersectionSubtitleCodes = getStringArrayIntersection(subtitleLanguageCodes);
  const intersectionPrimaryCodes = getStringArrayIntersection(primaryLanguageCodes);
  const intersectionTranslatedCodes = getStringArrayIntersection(translatedLanguageCodes);

  const intersectionDubbedLanguages = intersectionDubbedCodes
    .map((code) => vspPageData[0]?.dubbedLanguages?.find((lang) => lang.languageCode === code))
    .filter(Boolean) as NonNullable<VspCourseData['dubbedLanguages']>[number][];

  const intersectionTranslatedLanguages = intersectionTranslatedCodes
    .map((code) => vspPageData[0]?.translatedLanguages?.find((lang) => lang.code === code))
    .filter(Boolean) as VspCourseData['translatedLanguages'][number][];

  return {
    dubbedLanguages: intersectionDubbedLanguages,
    subtitleLanguages: intersectionSubtitleCodes,
    primaryLanguages: intersectionPrimaryCodes,
    translatedLanguages: intersectionTranslatedLanguages,
  };
};

export const getVspXdpV2Variant = () => {
  const variant = EnterpriseExperiments.get('vspXdpV2Variant');
  return {
    isV1WithProductCard: variant === 'v1WithProductCard',
    isV2WithProductCard: variant === 'v2WithProductCard',
    isControl: variant === 'control',
  };
};

export const getToolsAndLanguagesStrings = (courseId: string, moduleId: string) => {
  const toolsAndLanguages = moduleToolsAndLanguagesData[courseId]?.[moduleId] || [];

  return toolsAndLanguages
    .slice(0, TOOLS_AND_LANGUAGES_MAX_COUNT)
    .map((skill) =>
      skill.length <= TOOLS_AND_LANGUAGES_CHARACTER_LIMIT
        ? skill
        : `${skill.slice(0, TOOLS_AND_LANGUAGES_CHARACTER_LIMIT)}...`
    );
};
